import { motion } from 'framer-motion';
import { Check, Flag, MapPin } from 'lucide-react';

interface JourneyPathProps {
  total: number;
  current: number;
  screenLabels?: string[];
}

const JourneyPath = ({ total, current, screenLabels }: JourneyPathProps) => {
  const progress = total > 1 ? current / (total - 1) : 0;
  const currentLabel = screenLabels?.[current];

  return (
    <div
      className="w-full px-2"
      role="progressbar"
      aria-valuemin={0}
      aria-valuemax={total - 1}
      aria-valuenow={current}
      aria-label={`Assessment progress: step ${current + 1} of ${total}${currentLabel ? ` — ${currentLabel}` : ''}`}
    >
      {/* The trail */}
      <div className="relative h-10 flex items-center">
        {/* Background trail */}
        <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 rounded-full bg-muted/60" aria-hidden="true" />

        {/* Active trail glow */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 left-0 h-1 rounded-full"
          style={{ background: 'linear-gradient(90deg, hsl(var(--primary) / 0.3), hsl(var(--primary)))' }}
          initial={false}
          animate={{ width: `${progress * 100}%` }}
          transition={{ type: 'spring', stiffness: 120, damping: 20 }}
          aria-hidden="true"
        />

        {/* Waypoints */}
        {Array.from({ length: total }).map((_, i) => {
          const x = total > 1 ? (i / (total - 1)) * 100 : 50;
          const isVisited = i < current;
          const isActive = i === current;

          return (
            <motion.div
              key={i}
              aria-hidden="true"
              className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 flex flex-col items-center"
              style={{ left: `${x}%` }}
            >
              {/* Waypoint dot */}
              <motion.div
                className={`relative flex items-center justify-center rounded-full border-2 transition-colors ${
                  isActive
                    ? 'w-7 h-7 bg-primary border-primary shadow-lg'
                    : isVisited
                    ? 'w-4 h-4 bg-primary border-primary/60'
                    : 'w-3 h-3 bg-muted border-border'
                }`}
                initial={false}
              >
                {isActive && (
                  <>
                    {/* Breathing glow */}
                    <motion.div
                      className="absolute inset-0 rounded-full border-2 border-primary"
                      animate={{ scale: [1, 1.8], opacity: [0.6, 0] }}
                      transition={{ duration: 1.5, repeat: Infinity, ease: 'easeOut' }}
                    />
                    {/* Second slower pulse */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{ boxShadow: '0 0 12px 4px hsl(var(--primary) / 0.4)' }}
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    />
                    {/* Walker icon */}
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                    >
                      <MapPin className="w-3.5 h-3.5 text-primary-foreground" />
                    </motion.div>
                  </>
                )}
                {isVisited && (
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 300, damping: 15 }}
                  >
                    <Check className="w-2.5 h-2.5 text-primary-foreground" strokeWidth={3} />
                  </motion.div>
                )}
              </motion.div>
            </motion.div>
          );
        })}

        {/* Flag at end */}
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 right-0 translate-x-1/2"
          aria-hidden="true"
          animate={{
            y: current === total - 1 ? [0, -3, 0] : 0,
          }}
          transition={{ duration: 1.5, repeat: current === total - 1 ? Infinity : 0 }}
        >
          <Flag className={`w-3.5 h-3.5 ${current >= total - 1 ? 'text-primary' : 'text-muted-foreground/40'}`} />
        </motion.div>
      </div>

      {/* Step label */}
      <motion.div
        key={current}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mt-0.5"
        aria-hidden="true"
      >
        <span className="text-[10px] text-muted-foreground font-medium">
          Step {current + 1} of {total}{currentLabel ? ` — ${currentLabel}` : ''}
        </span>
      </motion.div>
    </div>
  );
};

export default JourneyPath;
